/**
 * #5966: which parent links a family removed, for screens that run with a
 * STAFF member's own login.
 *
 * RLS shows staff every parent_athlete_relationships row, including a link the
 * family removed through Family Access (revoked_guardian_no_access exempts
 * active staff), and the live view is service-role only. These helpers call
 * the staff-only database reads from migration 20261330_001. Each answers only
 * an active staff member of the gym. WHICH links are removed is the shared
 * rule (a removal in any family counts, the same as the live view the server
 * tools read); WHY is gym-private: `here` holds only the links removed in one
 * of this gym's own families, the only ones a screen may tag "Removed from
 * family".
 *
 * Browser-safe: no service-role client.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { fetchInChunks } from '@/utils/supabase/fetchInChunks';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('StaffRevokedLinks');

/**
 * The two reads arrive with migration 20261330_001. A web deploy can reach
 * production before that migration does (a migration may apply on merge or
 * not). Until it lands, "function not found" means no removals are known yet:
 * screens behave as they did before #5966 instead of failing. Any other error
 * still fails the read.
 */
function isMissingFunction(error: { code?: string } | null | undefined): boolean {
  return error?.code === 'PGRST202' || error?.code === '42883';
}

/** One RPC call; a "function not found" answer reads as an empty result. */
async function callTolerantOfMissingFunction<T>(
  call: PromiseLike<{ data: T[] | null; error: any }>
): Promise<{ data: T[] | null; error: { message: string } | null }> {
  const { data, error } = await call;
  if (error && isMissingFunction(error)) {
    logger.warn('Family Access removal check not available yet (migration 20261330_001); showing links unfiltered', error);
    return { data: [], error: null };
  }
  return { data, error };
}

/** `parent_id:athlete_id`, the key of one link. */
export function parentLinkKey(parentId: string, athleteId: string): string {
  return `${parentId}:${athleteId}`;
}

export interface StaffRevokedLinks {
  /** Every removed link among the athletes asked about: never show or use these as a parent. */
  all: Set<string>;
  /** The ones removed in one of this gym's own families: the only ones a screen may tag. */
  here: Set<string>;
}

/**
 * The removed links among these athletes, as `parent:athlete` keys. Null when
 * the read failed; each caller decides how to fail (pickers refuse, lists log).
 */
export async function readStaffRevokedLinkKeys(
  client: SupabaseClient<any, any, any>,
  athleteIds: readonly string[]
): Promise<StaffRevokedLinks | null> {
  const ids = [...new Set(athleteIds.filter(Boolean))];
  const result: StaffRevokedLinks = { all: new Set(), here: new Set() };
  if (ids.length === 0) return result;
  const { rows, error } = await fetchInChunks<{ parent_id: string; athlete_id: string; removed_here: boolean }>(ids, (chunk) =>
    callTolerantOfMissingFunction((client as any).rpc('staff_revoked_parent_links', { p_athlete_ids: chunk }))
  );
  if (error) return null;
  for (const r of rows) {
    const key = parentLinkKey(r.parent_id, r.athlete_id);
    result.all.add(key);
    if (r.removed_here === true) result.here.add(key);
  }
  return result;
}

/**
 * Split one adult's links into the ones still live, and the removed ones a
 * screen may show tagged (removed in this gym's own family). A link removed in
 * another gym's family is in neither: it is dropped without a reason.
 */
export function partitionAdultLinks<T extends { athlete_id: string }>(
  parentId: string,
  rows: readonly T[],
  revoked: StaffRevokedLinks
): { live: T[]; removedHere: T[] } {
  const live: T[] = [];
  const removedHere: T[] = [];
  for (const r of rows) {
    const key = parentLinkKey(parentId, r.athlete_id);
    if (!revoked.all.has(key)) live.push(r);
    else if (revoked.here.has(key)) removedHere.push(r);
  }
  return { live, removedHere };
}

/** Rows without the links the family removed. */
export function dropRevokedLinks<T extends { parent_id: string; athlete_id: string }>(
  rows: readonly T[],
  revoked: ReadonlySet<string>
): T[] {
  if (revoked.size === 0) return [...rows];
  return rows.filter((r) => !revoked.has(parentLinkKey(r.parent_id, r.athlete_id)));
}

export interface TeamRepRemovedAdult {
  /** True when they were removed from every athlete they had at this gym. */
  noAthletesAtGym: boolean;
  /** True when a removal was made in one of this gym's own families (the picker may say so). */
  removedHere: boolean;
}

/**
 * For the Team Representative picker: which of these people a family removed
 * from a child on this team (and who keep no live link to anyone on it), or
 * from every athlete they had at the gym. Throws when the read fails, so the
 * picker shows its error instead of an unmarked list.
 */
export async function readTeamRepRemovedAdults(
  client: SupabaseClient<any, any, any>,
  teamId: string,
  userIds: readonly string[]
): Promise<Map<string, TeamRepRemovedAdult>> {
  const ids = [...new Set(userIds.filter(Boolean))];
  const result = new Map<string, TeamRepRemovedAdult>();
  if (ids.length === 0) return result;
  const { rows, error } = await fetchInChunks<{ user_id: string; no_athletes_at_gym: boolean; removed_here: boolean }>(ids, (chunk) =>
    callTolerantOfMissingFunction((client as any).rpc('team_rep_removed_family_adults', { p_team: teamId, p_user_ids: chunk }))
  );
  if (error) throw new Error(`Could not check Family Access removals: ${error.message ?? error}`);
  for (const r of rows) result.set(r.user_id, { noAthletesAtGym: r.no_athletes_at_gym === true, removedHere: r.removed_here === true });
  return result;
}
